import {Component, OnInit} from '@angular/core';
import {DatePipe, NgClass} from '@angular/common';
import {RouterLink} from '@angular/router';
import {forkJoin} from 'rxjs';
import {SubmissionHttpService} from '../data-access/submission-http.service';
import {FormHttpService} from '../data-access/form-http.service';
import {EmployeeHttpService} from '../../company/x-http-requests/employee-http.service';
import {Submission} from '../shared/models/submission.model';
import {submissionStatusClass, submissionStatusLabel} from '../shared/services/submission-display';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AlertLevel} from '../../../enums/alert-level';

/**
 * Lists form submissions the caller may see (own for employees; the whole company for management/data roles).
 * Metadata only — opening one fetches its (masked) values. Form titles + employee names are resolved for display.
 * See {@link SubmissionDetailComponent}.
 */
@Component({
  selector: 'forms-submission-list',
  standalone: true,
  imports: [DatePipe, NgClass, RouterLink],
  template: `
    <div class="absolute inset-0 overflow-y-auto p-3 sm:p-4 lg:p-6">
      <div class="mx-auto w-full max-w-5xl flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <a routerLink="/company/forms"
             class="inline-flex w-fit items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
            </svg>
            Formulare
          </a>
          <h1 class="text-xl font-bold tracking-tight text-gray-900">Formulareingaben</h1>
        </div>

        @if (loading) {
          <div class="flex items-center justify-center py-20">
            <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
            </svg>
          </div>
        } @else {
          <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 text-sm">
                <thead class="bg-gray-50">
                  <tr class="text-left text-xs uppercase tracking-wide text-gray-500">
                    <th class="px-4 py-3 font-semibold">Formular</th>
                    <th class="px-4 py-3 font-semibold">Mitarbeiter</th>
                    <th class="px-4 py-3 font-semibold">Status</th>
                    <th class="px-4 py-3 font-semibold">Eingereicht</th>
                    <th class="px-4 py-3"><span class="sr-only">Prüfung</span></th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  @for (s of submissions; track s.id) {
                    <tr [routerLink]="[s.id]" class="cursor-pointer transition-colors hover:bg-gray-50">
                      <td class="px-4 py-3">
                        <span class="font-semibold text-gray-900">{{ formTitle(s) }}</span>
                        <span class="ml-1 text-xs text-gray-400">v{{ s.formVersion }}</span>
                      </td>
                      <td class="px-4 py-3 text-gray-700">{{ employeeName(s) }}</td>
                      <td class="px-4 py-3">
                        <span class="rounded-full px-2.5 py-0.5 text-xs font-semibold" [ngClass]="statusClass(s.status)">{{ statusLabel(s.status) }}</span>
                      </td>
                      <td class="px-4 py-3 text-gray-500">{{ s.submittedAt ? (s.submittedAt | date: 'dd.MM.yyyy HH:mm') : '–' }}</td>
                      <td class="px-4 py-3 text-right">
                        @if (s.needsReview) {
                          <span title="{{ s.reviewReason }}" class="rounded-full bg-yellow-50 px-2.5 py-0.5 text-xs font-semibold text-yellow-700">Prüfung nötig</span>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            @if (submissions.length === 0) {
              <p class="text-sm text-gray-400 text-center py-10">Keine Eingaben gefunden.</p>
            }
          </div>
        }
      </div>
    </div>
  `
})
export class SubmissionListComponent implements OnInit {
  protected loading = true;
  protected submissions: Submission[] = [];
  private titles: Record<string, string> = {};
  private employeeNames: Record<string, string> = {};

  protected readonly statusLabel = submissionStatusLabel;
  protected readonly statusClass = submissionStatusClass;

  constructor(
    private http: SubmissionHttpService,
    private forms: FormHttpService,
    private employees: EmployeeHttpService,
    private gas: GlobalAlertService
  ) {
  }

  ngOnInit(): void {
    // Employees may 403 the company employee list (they only see their own submissions) — fail soft to ids.
    forkJoin({
      list: this.http.list(),
      defs: this.forms.listDefinitions(),
      // all=true: historical submissions by now-former employees must still resolve to a name.
      // stats=false: only the name is read below, so skip the worktime statistics the server would otherwise
      // compute over every shift of every employee ever employed here (#457).
      emps: this.employees.listNl(undefined, true, false)
    }).subscribe({
      next: ({list, defs, emps}) => {
        this.titles = Object.fromEntries(defs.map(d => [d.id, d.title]));
        this.employeeNames = Object.fromEntries(emps.map(e => [e.id, `${e.firstName} ${e.lastName}`.trim() || e.name]));
        this.submissions = list;
        this.loading = false;
      },
      error: () => {
        // Fall back to just the submissions if the lookups fail (e.g. employee-scoped caller).
        this.http.list().subscribe({
          next: list => {
            this.submissions = list;
            this.loading = false;
          },
          error: () => {
            this.loading = false;
            this.gas.createAndShow('Fehler', 'Eingaben konnten nicht geladen werden.', AlertLevel.error);
          }
        });
      }
    });
  }

  protected formTitle(s: Submission): string {
    return this.titles[s.formId] ?? `Formular ${s.formId.substring(0, 8)}`;
  }

  protected employeeName(s: Submission): string {
    return this.employeeNames[s.employeeId] ?? `${s.employeeId.substring(0, 8)}…`;
  }
}
